/**
 * Affärer/Pipeline – flöde i stället för KSEK.
 *
 * Ren logik utan Firestore: konstanter, tratt, perioder och ekonomitabell.
 * Allt som räknar leads ska gå genom funktionerna här, så att en siffra och
 * tavlan den filtrerar alltid räknar på samma sätt.
 *
 * Skedesnycklar som i CRMLeads.js. 'sandbox' är ett gammalt skede som visas
 * som Demo och räknas som Demo överallt här.
 */

// ── Konstanter ──────────────────────────────────────────────────────────────

/** Skedena i flödesordning. Förlorad och Vilande ligger utanför flödet. */
export const FLODESSKEDEN = ['identified', 'qualified', 'demo', 'proposal', 'negotiation', 'pilot', 'won'];

/** Aktiva skeden. Vilande räknas som avslutad: kunden har i princip tackat nej.
 *  Pilot (pilot/kampanj pågår) är aktivt men inte vunnet. */
export const AKTIVA_SKEDEN = ['identified', 'qualified', 'demo', 'sandbox', 'proposal', 'negotiation', 'pilot'];

/** Avslutade skeden som visas bredvid tratten, inte i den. */
export const SKEDEN_UTANFOR_TRATTEN = ['lost', 'dormant'];

/** Storleksklass i KSEK per år: S under 100, M 100–500, L över 500. */
export const STORLEK_GRANSER_KSEK = { S_UNDER: 100, M_TILL_OCH_MED: 500 };

export const STORLEKAR = [
  { key: 'S', label: 'S', beskrivning: `under ${STORLEK_GRANSER_KSEK.S_UNDER} KSEK` },
  { key: 'M', label: 'M', beskrivning: `${STORLEK_GRANSER_KSEK.S_UNDER}–${STORLEK_GRANSER_KSEK.M_TILL_OCH_MED} KSEK` },
  { key: 'L', label: 'L', beskrivning: `över ${STORLEK_GRANSER_KSEK.M_TILL_OCH_MED} KSEK` },
];

/**
 * Ett lead som flyttas till Vilande får återkontakt så här många månader
 * fram, om det inte redan har en senare uppföljning.
 */
export const VILANDE_ATERKONTAKT_MANADER = 6;

/** nastaSteg som följer med den automatiska återkontakten. */
export const VILANDE_NASTA_STEG = 'Återkontakt';

/**
 * Skeden ett lead kan lämna Demo för när demon har hållits. Demo betyder
 * "demo bokad, inte hållen". Vilande räknas bara om demostatus är Genomförd.
 */
export const DEMO_GENOMFORD_TILL = ['proposal', 'negotiation', 'pilot', 'won'];

/** Färg på "dagar i skede" på kanbankortet: neutral under ORANGE, röd över ROD. */
export const DAGAR_I_SKEDE_GRANS = { ORANGE: 21, ROD: 45 };

/**
 * Mål per period för aktivitetsraden. Visas inte än – finns här så att målen
 * kan läggas in utan att räknelogiken behöver röras.
 */
export const AKTIVITETSMAL = { bokadeMoten: null, genomfordaDemos: null, skickadeOfferter: null };

/**
 * Så många leads med fullständig historik (skapade efter att loggningen
 * började) behövs innan tratten räknar konvertering från historiken i stället
 * för att uppskatta den.
 */
export const KONVERTERING_MIN_UNDERLAG = 10;

export const OFFERT_STATUSAR = [
  { key: 'skickad',    label: 'Skickad',    color: '#1565C0', bg: '#E3F2FD' },
  { key: 'accepterad', label: 'Accepterad', color: '#2E7D32', bg: '#E8F5E9' },
  { key: 'avböjd',     label: 'Avböjd',     color: '#C62828', bg: '#FFEBEE' },
];

export const PERIODER = [
  { key: 'vecka', label: 'Denna vecka', forra: 'förra veckan' },
  { key: 'manad', label: 'Denna månad', forra: 'förra månaden' },
  { key: '30d',   label: '30 d',        forra: '30 d innan' },
];

const DAG_MS = 86400000;

// Snabbloggen "Kunden kom inte" sparas som möte. Den är inget bokat möte.
const UTEBLIVET_MOTE_PREFIX = 'Mötet blev inte av';

// ── Grundfunktioner ─────────────────────────────────────────────────────────

/** Firestore Timestamp, {seconds}, Date, ISO-sträng eller ms → ms. Annars null. */
export function toMillis(val) {
  if (val == null || val === '') return null;
  if (typeof val.toMillis === 'function') return val.toMillis();
  if (typeof val.seconds === 'number') return val.seconds * 1000;
  if (val instanceof Date) return val.getTime();
  if (typeof val === 'number') return val;
  if (typeof val === 'string') {
    // 'YYYY-MM-DD' tolkas som lokal midnatt, inte UTC.
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(val);
    if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3])).getTime();
    const t = Date.parse(val);
    return Number.isNaN(t) ? null : t;
  }
  return null;
}

/** Lokalt datum som 'YYYY-MM-DD'. */
export function lokalDatum(ms = Date.now()) {
  const d = new Date(ms);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function normaliseraSkede(status) {
  return status === 'sandbox' ? 'demo' : status;
}

/** Plats i flödet, eller -1 för Förlorad, Vilande och okänt. */
export function skedesIndex(status) {
  return FLODESSKEDEN.indexOf(normaliseraSkede(status));
}

export function arAktiv(lead) {
  return AKTIVA_SKEDEN.includes(lead.status);
}

/** Värdefältet omräknat till KSEK per år. Per projekt räknas som det står. */
export function arsvardeKsek(value, unit) {
  const v = Number(value);
  if (!Number.isFinite(v) || v <= 0) return null;
  return unit === 'manad' ? v * 12 : v;
}

/** KSEK per år → 'S' | 'M' | 'L', eller null om värdet saknas. */
export function storlekFranKsek(ksek) {
  const v = Number(ksek);
  if (!Number.isFinite(v) || v <= 0) return null;
  if (v < STORLEK_GRANSER_KSEK.S_UNDER) return 'S';
  if (v <= STORLEK_GRANSER_KSEK.M_TILL_OCH_MED) return 'M';
  return 'L';
}

/** När leadet kom in i sitt nuvarande skede. Faller tillbaka som backfillen. */
export function skedeSedanMs(lead) {
  const historik = lead.skedeshistorik || [];
  const senasteByte = historik.reduce((max, h) => Math.max(max, toMillis(h.tid) || 0), 0);
  return toMillis(lead.skedeSedan)
    ?? (senasteByte || null)
    ?? toMillis(lead.updatedAt)
    ?? toMillis(lead.createdAt);
}

export function dagarISkede(lead, now = Date.now()) {
  const sedan = skedeSedanMs(lead);
  if (sedan == null) return null;
  return Math.max(0, Math.floor((now - sedan) / DAG_MS));
}

/** 'neutral' | 'orange' | 'rod' */
export function dagarISkedeTon(dagar) {
  if (dagar == null) return 'neutral';
  if (dagar > DAGAR_I_SKEDE_GRANS.ROD) return 'rod';
  if (dagar >= DAGAR_I_SKEDE_GRANS.ORANGE) return 'orange';
  return 'neutral';
}

/** Har leadet en uppföljning idag eller senare? */
export function harFramtidaSteg(lead, idag = lokalDatum()) {
  return !!lead.nextFollowUp && lead.nextFollowUp >= idag;
}

export function arForfallen(lead, idag = lokalDatum()) {
  return !!lead.nextFollowUp && lead.nextFollowUp < idag;
}

/**
 * Aktivt lead vars uppföljning har passerat. Räknar siffran på klockan i
 * toppraden och filtrerar tavlan när man klickar på den.
 */
export function harForfallenUppfoljning(lead, idag = lokalDatum()) {
  return arAktiv(lead) && arForfallen(lead, idag);
}

/** Vilande lead vars återkontakt är idag eller har passerat. */
export function arVilandeAttAterkontakta(lead, idag = lokalDatum()) {
  return lead.status === 'dormant' && !!lead.nextFollowUp && lead.nextFollowUp <= idag;
}

/**
 * 'YYYY-MM-DD' plus n månader. Dagen kläms till månadens sista, så att
 * 31 augusti + 6 blir 28/29 februari och inte 3 mars.
 */
export function plusManader(datum, n) {
  const [y, m, d] = datum.split('-').map(Number);
  const sista = new Date(y, m - 1 + n + 1, 0).getDate();
  return lokalDatum(new Date(y, m - 1 + n, Math.min(d, sista)).getTime());
}

/**
 * Återkontaktsdatum för ett lead som flyttas till Vilande: idag + 6 månader.
 * null om leadet redan har en uppföljning det datumet eller senare.
 */
export function vilandeAterkontakt(nuvarandeUppfoljning, now = Date.now()) {
  const datum = plusManader(lokalDatum(now), VILANDE_ATERKONTAKT_MANADER);
  if (typeof nuvarandeUppfoljning === 'string' && nuvarandeUppfoljning >= datum) return null;
  return datum;
}

// ── Uppföljning ─────────────────────────────────────────────────────────────
//
// Uppföljningen finns bara på leadet. Aktivitetsdialogen och
// redigeringsdialogen visar och skriver samma fält:
//   nastaSteg                 fritext
//   nextFollowUp              'YYYY-MM-DD'
//   nextFollowUpTime          'HH:MM' eller ''
//   nextFollowUpStatus        'preliminar' | 'bokad' | ''
//   nextFollowUpReminder      mejlpåminnelse på/av
//   nextFollowUpReminderDays  så många dagar före uppföljningen (0 = samma dag)
//   nextFollowUpReminderTime  'HH:MM' då påminnelsen skickas
//   nextFollowUpReminderUid   vem som får påminnelsen (den som slog på den)
// Aktiviteternas gamla followUp*/reminder*-fält ligger kvar i databasen men
// används inte längre, utom för pilotpåminnelserna (pilotPaminnelser.js).

export const UPPFOLJNING_STATUSAR = [
  { key: '', label: 'Ingen status' },
  { key: 'preliminar', label: 'Preliminär' },
  { key: 'bokad', label: 'Bokad' },
];

export const PAMINNELSE_DAGAR_FORE = [
  { key: 0, label: 'Samma dag' },
  { key: 1, label: 'Dagen innan' },
  { key: 2, label: 'Två dagar innan' },
  { key: 7, label: 'En vecka innan' },
];

/** Leadets uppföljningsfält med standardvärden, för formulären. */
export function uppfoljningFranLead(lead = {}) {
  return {
    nastaSteg: lead.nastaSteg || '',
    nextFollowUp: lead.nextFollowUp || '',
    nextFollowUpTime: lead.nextFollowUpTime || '',
    nextFollowUpStatus: lead.nextFollowUpStatus || '',
    nextFollowUpReminder: !!lead.nextFollowUpReminder,
    nextFollowUpReminderDays: lead.nextFollowUpReminderDays ?? 1,
    nextFollowUpReminderTime: lead.nextFollowUpReminderTime || '07:00',
  };
}

/**
 * Det som skrivs till leadet när ett formulär med uppföljningen sparas.
 * Utan datum finns varken tid, status eller påminnelse. Påminnelsen går till
 * den som slog på den; sparar någon annan om den behålls mottagaren.
 */
export function uppfoljningPatch(falt, lead = {}, uid = '') {
  const f = uppfoljningFranLead(falt);
  const datum = f.nextFollowUp;
  const paminnelse = !!datum && f.nextFollowUpReminder;
  return {
    nastaSteg: f.nastaSteg.trim(),
    nextFollowUp: datum,
    nextFollowUpTime: datum ? f.nextFollowUpTime : '',
    nextFollowUpStatus: datum ? f.nextFollowUpStatus : '',
    nextFollowUpReminder: paminnelse,
    nextFollowUpReminderDays: Number(f.nextFollowUpReminderDays) || 0,
    nextFollowUpReminderTime: f.nextFollowUpReminderTime,
    nextFollowUpReminderUid: paminnelse
      ? (lead.nextFollowUpReminder && lead.nextFollowUpReminderUid) || uid
      : '',
  };
}

/**
 * När leadets mejlpåminnelse ska skickas: { datum: 'YYYY-MM-DD', timme }
 * eller null. Samma regel finns i sendActivityReminders (functions/index.js).
 */
export function paminnelseFor(lead) {
  if (!lead?.nextFollowUpReminder || !lead.nextFollowUp) return null;
  const [y, m, d] = lead.nextFollowUp.split('-').map(Number);
  const dagar = Number(lead.nextFollowUpReminderDays) || 0;
  const datum = lokalDatum(new Date(y, m - 1, d - dagar, 12).getTime());
  const timme = Number((lead.nextFollowUpReminderTime || '07:00').split(':')[0]);
  return { datum, timme: Number.isFinite(timme) ? timme : 7 };
}

/** "Preliminär" / "Bokad" / '' för leadkortet. */
export function uppfoljningStatusText(lead) {
  return UPPFOLJNING_STATUSAR.find(s => s.key && s.key === lead?.nextFollowUpStatus)?.label || '';
}

/** Tidigaste loggade skedesbyte bland alla leads, i ms, eller null. */
export function historikMatsFran(leads) {
  let min = null;
  for (const l of leads) {
    for (const h of l.skedeshistorik || []) {
      const t = toMillis(h.tid);
      if (t != null && (min == null || t < min)) min = t;
    }
  }
  return min;
}

// ── Tratt ───────────────────────────────────────────────────────────────────

/** Högsta skede leadet nått enligt nuvarande skede och historiken. */
function hogstaIndex(lead) {
  let max = skedesIndex(lead.status);
  for (const h of lead.skedeshistorik || []) {
    max = Math.max(max, skedesIndex(h.till), h.fran != null ? skedesIndex(h.fran) : -1);
  }
  return max;
}

/** Leadet är skapat efter att loggningen började, så hela resan är känd. */
function harFullHistorik(lead) {
  return (lead.skedeshistorik || []).some(h => h.fran == null);
}

/**
 * Antal per skede och konvertering mellan skedena. Vilande räknas för sig,
 * med hur många av dem som ska återkontaktas idag eller tidigare.
 *
 * Med historik: andel av leads som nått skede N som också nått N+1, räknat på
 * leads vars hela resa är loggad. Utan tillräcklig historik: antal i N+1 och
 * senare delat med antal i N och senare, på nuvarande skede (uppskattad).
 */
export function tratt(leads, now = Date.now()) {
  const idag = lokalDatum(now);
  const antal = Object.fromEntries(FLODESSKEDEN.map(k => [k, 0]));
  let forlorad = 0;
  let vilande = 0;
  let vilandeAttAterkontakta = 0;
  for (const l of leads) {
    const s = normaliseraSkede(l.status);
    if (s in antal) antal[s] += 1;
    else if (s === 'lost') forlorad += 1;
    else if (s === 'dormant') {
      vilande += 1;
      if (arVilandeAttAterkontakta(l, idag)) vilandeAttAterkontakta += 1;
    }
  }

  const underlag = leads.filter(harFullHistorik);
  const kalla = underlag.length >= KONVERTERING_MIN_UNDERLAG ? 'historik' : 'uppskattad';

  let nadd;
  if (kalla === 'historik') {
    const max = underlag.map(hogstaIndex);
    nadd = FLODESSKEDEN.map((_, i) => max.filter(m => m >= i).length);
  } else {
    const idx = leads.map(l => skedesIndex(l.status)).filter(i => i >= 0);
    nadd = FLODESSKEDEN.map((_, i) => idx.filter(m => m >= i).length);
  }

  const konvertering = FLODESSKEDEN.slice(0, -1).map((_, i) => (
    nadd[i] > 0 ? nadd[i + 1] / nadd[i] : null
  ));

  return { antal, forlorad, vilande, vilandeAttAterkontakta, konvertering, kalla, underlag: underlag.length };
}

// ── Perioder och aktivitet ──────────────────────────────────────────────────

/** { start, slut, forraStart, forraSlut } i ms. Perioden pågår till slut (exkl). */
export function periodIntervall(period, now = Date.now()) {
  const d = new Date(now);
  if (period === 'vecka') {
    const dagIVeckan = (d.getDay() + 6) % 7; // måndag = 0
    const start = new Date(d.getFullYear(), d.getMonth(), d.getDate() - dagIVeckan).getTime();
    const slut = new Date(d.getFullYear(), d.getMonth(), d.getDate() - dagIVeckan + 7).getTime();
    const forraStart = new Date(d.getFullYear(), d.getMonth(), d.getDate() - dagIVeckan - 7).getTime();
    return { start, slut, forraStart, forraSlut: start };
  }
  if (period === 'manad') {
    const start = new Date(d.getFullYear(), d.getMonth(), 1).getTime();
    const slut = new Date(d.getFullYear(), d.getMonth() + 1, 1).getTime();
    const forraStart = new Date(d.getFullYear(), d.getMonth() - 1, 1).getTime();
    return { start, slut, forraStart, forraSlut: start };
  }
  const start = now - 30 * DAG_MS;
  return { start, slut: now + 1, forraStart: now - 60 * DAG_MS, forraSlut: start };
}

/** Tidigaste tidpunkt någon period behöver data från (för att ladda möten). */
export function tidigastePeriodStart(now = Date.now()) {
  return Math.min(...PERIODER.map(p => periodIntervall(p.key, now).forraStart));
}

const inom = (t, a, b) => t != null && t >= a && t < b;

/** Möten räknas när de bokades, inte när de hålls. Uteblivna räknas inte. */
export function arBokatMote(aktivitet) {
  return aktivitet.type === 'meeting'
    && !String(aktivitet.summary || '').startsWith(UTEBLIVET_MOTE_PREFIX);
}

export function raknaMoten(moten, a, b) {
  return moten.filter(m => arBokatMote(m) && inom(toMillis(m.createdAt), a, b)).length;
}

/** Skedesbytet visar att demon hölls: Demo → Offert/Förhandling/Vunnen, eller → Vilande med demon Genomförd. */
function arGenomfordDemo(lead, h) {
  if (normaliseraSkede(h.fran) !== 'demo') return false;
  if (DEMO_GENOMFORD_TILL.includes(h.till)) return true;
  return h.till === 'dormant' && lead.demoStatus === 'genomförd';
}

/**
 * Genomförda demos: antal leads som under [a, b) lämnat Demo för Offert,
 * Förhandling eller Vunnen, enligt skedeshistoriken. Demo → Vilande räknas
 * bara när demostatus är Genomförd – annars hölls demon aldrig.
 */
export function raknaGenomfordaDemos(leads, a, b) {
  return leads.filter(l => (l.skedeshistorik || []).some(h =>
    arGenomfordDemo(l, h) && inom(toMillis(h.tid), a, b)
  )).length;
}

export function raknaOfferter(offerter, a, b) {
  return offerter.filter(o => inom(toMillis(o.datum), a, b)).length;
}

/** Värden för aktivitetsraden, med föregående period för jämförelse. */
export function aktivitetPerPeriod({ period, leads, moten, offerter, now = Date.now() }) {
  const { start, slut, forraStart, forraSlut } = periodIntervall(period, now);
  const leadIds = new Set(leads.map(l => l.id));
  const m = moten.filter(x => leadIds.has(x.leadId));
  const o = offerter.filter(x => leadIds.has(x.leadId));
  return {
    bokadeMoten:      { nu: raknaMoten(m, start, slut),      forra: raknaMoten(m, forraStart, forraSlut) },
    genomfordaDemos:  { nu: raknaGenomfordaDemos(leads, start, slut), forra: raknaGenomfordaDemos(leads, forraStart, forraSlut) },
    skickadeOfferter: { nu: raknaOfferter(o, start, slut),   forra: raknaOfferter(o, forraStart, forraSlut) },
  };
}

/** Om genomförda demos kan mätas för perioden (historiken täcker den föregående perioden). */
export function demosMatbara(leads, period, now = Date.now()) {
  const matsFran = historikMatsFran(leads);
  return matsFran != null && matsFran <= periodIntervall(period, now).forraStart;
}

// ── Offerter ────────────────────────────────────────────────────────────────

/** Nyast först: datum, sedan när offerten registrerades. */
export function sorteraOfferter(list) {
  return [...list].sort((a, b) =>
    (toMillis(b.datum) ?? 0) - (toMillis(a.datum) ?? 0)
    || (toMillis(b.skapadAt) ?? 0) - (toMillis(a.skapadAt) ?? 0));
}

/** { [leadId]: offerter nyast först } */
export function offerterPerLead(offerter) {
  const map = {};
  for (const o of offerter) (map[o.leadId] ||= []).push(o);
  for (const k of Object.keys(map)) map[k] = sorteraOfferter(map[k]);
  return map;
}

function harBelopp(o) {
  return o.beloppKSEK != null && o.beloppKSEK !== '' && Number.isFinite(Number(o.beloppKSEK));
}

// ── Ekonomi ─────────────────────────────────────────────────────────────────

/**
 * Leadets värde i ekonomivyn: senaste skickade offertens belopp, annars det
 * gamla värdefältet omräknat till per år.
 * kalla: 'offert' | 'värdefält' | null
 */
export function leadVarde(lead, offerter = []) {
  const offert = sorteraOfferter(offerter).find(harBelopp);
  if (offert) return { ksek: Number(offert.beloppKSEK), kalla: 'offert', offert };
  const ar = arsvardeKsek(lead.estimatedValue, lead.revenueUnit);
  if (ar != null) return { ksek: ar, kalla: 'värdefält' };
  return { ksek: 0, kalla: null };
}

/**
 * Rader för ekonomitabellen: en per produkt med vunna eller aktiva leads, plus
 * en rad för leads utan produkt. Summan av raderna är summan av alla leads.
 */
export function ekonomiRader(leads, allaProdukter, offerterMap = {}) {
  const produkter = new Map(allaProdukter.map(p => [p.id, p]));
  const rader = new Map();
  const radFor = l => {
    const p = produkter.get(l.productId);
    const key = p ? p.id : '__utan__';
    if (!rader.has(key)) {
      rader.set(key, {
        key,
        namn: p ? `${p.name}${p.active === false ? ' (inaktiv)' : ''}` : 'Ingen produkt',
        utanProdukt: !p,
        wonCount: 0, wonKSEK: 0, activeCount: 0, activeKSEK: 0,
        kallor: { offert: 0, 'värdefält': 0, saknas: 0 },
        leads: [],
      });
    }
    return rader.get(key);
  };

  for (const l of leads) {
    const won = l.status === 'won';
    if (!won && !arAktiv(l)) continue;
    const v = leadVarde(l, offerterMap[l.id]);
    const r = radFor(l);
    if (won) { r.wonCount += 1; r.wonKSEK += v.ksek; } else { r.activeCount += 1; r.activeKSEK += v.ksek; }
    r.kallor[v.kalla || 'saknas'] += 1;
    r.leads.push({ lead: l, ...v });
  }

  const list = [...rader.values()].sort((a, b) =>
    (a.utanProdukt - b.utanProdukt) || a.namn.localeCompare(b.namn, 'sv'));
  const totalt = list.reduce((t, r) => ({
    wonCount: t.wonCount + r.wonCount, wonKSEK: t.wonKSEK + r.wonKSEK,
    activeCount: t.activeCount + r.activeCount, activeKSEK: t.activeKSEK + r.activeKSEK,
  }), { wonCount: 0, wonKSEK: 0, activeCount: 0, activeKSEK: 0 });
  return { rader: list, totalt };
}
